import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import useJobStore from '../../store/jobStore';
import { useAuthStore } from '../../store';

/**
 * Breadcrumb — auto-generated navigation trail that lives in DashboardLayout.
 *
 * Static segments: resolved from the ROUTE_MAP below.
 * Dynamic segments: resolved by fetching labels from Zustand stores.
 *
 * Rendered as a thin bar below the sidebar-header boundary, only visible on
 * pages deeper than top-level (i.e. not shown on /dashboard, /jobs, etc.)
 */

// ─── Static route label map ────────────────────────────────────────────────
// Keys are the raw path segment strings. Values are the human-readable labels.
const STATIC_LABELS = {
  dashboard: 'Dashboard',
  jobs: 'Jobs',
  create: 'Create',
  matches: 'Matches',
  candidates: 'Candidates',
  bands: 'Match Bands',
  interviews: 'Interviews',
  schedule: 'Schedule Interview',
  assessments: 'Assessments',
  offers: 'Offers',
  onboarding: 'Onboarding',
  reports: 'Reports',
  profile: 'Profile',
  config: 'Configuration',
  roles: 'Roles',
  users: 'Admin RBAC',
};

// Regex patterns to identify dynamic param segments (UUIDs or numeric IDs)
const IS_DYNAMIC_PARAM = /^[0-9a-f-]{8,}$|^\d+$/i;

/**
 * Build a crumb trail from the current pathname.
 * Returns an array of { label, path, isDynamic } objects.
 */
function buildCrumbs(pathname) {
  const segments = pathname.replace(/^\//, '').split('/').filter(Boolean);
  const crumbs = [];
  let builtPath = '';

  for (const segment of segments) {
    builtPath += `/${segment}`;
    const isDynamic = IS_DYNAMIC_PARAM.test(segment);
    crumbs.push({
      segment,
      path: builtPath,
      isDynamic,
      label: isDynamic ? segment : (STATIC_LABELS[segment] || segment),
    });
  }

  return crumbs;
}

/**
 * Determine if the breadcrumb bar should be shown.
 * Hidden on top-level single-segment routes (Dashboard, Jobs, etc.)
 * and on the root redirect.
 */
function shouldShowBreadcrumb(pathname) {
  const segments = pathname.replace(/^\//, '').split('/').filter(Boolean);
  // Show only when there are 2+ segments (e.g. /jobs/:id or /jobs/create)
  return segments.length >= 2;
}

// ─── Component ─────────────────────────────────────────────────────────────

const Breadcrumb = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentJob } = useJobStore();
  const { user } = useAuthStore();

  // Resolved labels for dynamic segments — keyed by segment value
  const [dynamicLabels, setDynamicLabels] = useState({});

  const crumbs = buildCrumbs(location.pathname);
  const visible = shouldShowBreadcrumb(location.pathname);

  // ── Resolve dynamic labels when the job store has a current job loaded ──
  useEffect(() => {
    const nextLabels = {};

    // Map job IDs → job title from the currently loaded job
    if (currentJob?.id && currentJob?.title) {
      nextLabels[String(currentJob.id)] = currentJob.title;
    }

    // Map user's own ID → their name (in case profile routes use it)
    if (user?.id && (user?.name || user?.firstName)) {
      nextLabels[String(user.id)] = user.name || `${user.firstName} ${user.lastName || ''}`.trim();
    }

    setDynamicLabels((prev) => ({ ...prev, ...nextLabels }));
  }, [currentJob, user]);

  if (!visible) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-1 border-b border-slate-200/70 bg-white/80 px-4 py-2.5 text-xs backdrop-blur-sm sm:px-6 lg:px-7"
    >
      {/* Home icon → always goes to dashboard */}
      <button
        onClick={() => navigate('/dashboard')}
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-slate-400 transition hover:text-[#0b1f3a]"
        aria-label="Go to Dashboard"
      >
        <Home size={13} />
      </button>

      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;

        // Resolve the label: prefer dynamic store label, then static map, then raw segment
        const resolvedLabel = crumb.isDynamic
          ? (dynamicLabels[crumb.segment] || crumb.label)
          : crumb.label;

        // Truncate long dynamic labels (job titles can be very long)
        const displayLabel = resolvedLabel.length > 32
          ? `${resolvedLabel.slice(0, 32)}…`
          : resolvedLabel;

        return (
          <React.Fragment key={crumb.path}>
            <ChevronRight size={12} className="shrink-0 text-slate-300" aria-hidden="true" />

            {isLast ? (
              // Current page — not a link
              <span
                className="max-w-[200px] truncate font-medium text-[#0b1f3a]"
                aria-current="page"
                title={resolvedLabel}
              >
                {displayLabel}
              </span>
            ) : (
              // Ancestor — clickable link
              <button
                onClick={() => navigate(crumb.path)}
                className="max-w-[160px] truncate text-slate-500 transition hover:text-[#0b1f3a]"
                title={resolvedLabel}
              >
                {displayLabel}
              </button>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default Breadcrumb;
